import { CalendarDays, Megaphone, NotebookPen, Siren, TreePalm, Wallet, type LucideIcon } from 'lucide-react'
import i18n, { intlLocale } from '@/i18n'
import type {
  AnnouncementAudience,
  AnnouncementCategory,
  AnnouncementPriority,
  AnnouncementStatus,
} from './types'

type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info'

const t = (key: string, options?: Record<string, unknown>) => i18n.t(key, { ns: 'announcements', ...options })

/**
 * Labels below are English source text (translation keys, ns `announcements`). Translate them
 * at render: `t(meta.label)` in components, `tAny(meta.label)` elsewhere.
 */

/** Category → label, icon and token-based chip/icon classes. */
export const categoryMeta: Record<
  AnnouncementCategory,
  { label: string; icon: LucideIcon; chip: string; tile: string }
> = {
  general: {
    label: 'General',
    icon: Megaphone,
    chip: 'bg-brand-50 text-brand-700 ring-brand-200/70',
    tile: 'bg-brand-50 text-brand-600',
  },
  event: {
    label: 'Event',
    icon: CalendarDays,
    chip: 'bg-accent-400/10 text-accent-600 ring-accent-400/30',
    tile: 'bg-accent-400/10 text-accent-600',
  },
  holiday: {
    label: 'Holiday',
    icon: TreePalm,
    chip: 'bg-success-50 text-success-700 ring-success-500/20',
    tile: 'bg-success-50 text-success-600',
  },
  exam: {
    label: 'Exam',
    icon: NotebookPen,
    chip: 'bg-info-50 text-info-700 ring-info-500/20',
    tile: 'bg-info-50 text-info-600',
  },
  fee: {
    label: 'Fees',
    icon: Wallet,
    chip: 'bg-warning-50 text-warning-700 ring-warning-500/25',
    tile: 'bg-warning-50 text-warning-600',
  },
  urgent: {
    label: 'Urgent',
    icon: Siren,
    chip: 'bg-danger-50 text-danger-700 ring-danger-500/20',
    tile: 'bg-danger-50 text-danger-600',
  },
}

export const priorityMeta: Record<AnnouncementPriority, { label: string; tone: Tone }> = {
  normal: { label: 'Normal', tone: 'neutral' },
  important: { label: 'Important', tone: 'warning' },
  urgent: { label: 'Urgent', tone: 'danger' },
}

export const statusMeta: Record<AnnouncementStatus, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  scheduled: { label: 'Scheduled', tone: 'info' },
  published: { label: 'Published', tone: 'success' },
  expired: { label: 'Expired', tone: 'warning' },
}

export const audienceLabel: Record<AnnouncementAudience, string> = {
  all: 'Everyone',
  staff: 'Staff',
  students: 'Students',
  parents: 'Parents',
  class: 'Specific classes',
}

const rtfCache = new Map<string, Intl.RelativeTimeFormat>()
function rtf(): Intl.RelativeTimeFormat {
  const locale = intlLocale()
  let f = rtfCache.get(locale)
  if (!f) {
    f = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
    rtfCache.set(locale, f)
  }
  return f
}
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
]

/** "3 hours ago", "in 2 days", "just now" — in the active interface language. */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000)
  if (Number.isNaN(seconds)) return ''
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return rtf().format(Math.trunc(seconds / size), unit)
  }
  return t('just now')
}

/** ISO string → value for <input type="datetime-local"> in the browser's zone. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** datetime-local value → ISO-8601 with offset (what the API expects), or null. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

export { formatBytes } from '@/utils/format'

/** Where "View all" leads for the active portal. */
export function noticesHref(portal: string | null, canManage: boolean): string {
  if (portal === 'staff') return '/staff/notices'
  if (portal === 'family') return '/portal/notices'
  return canManage ? '/admin/announcements' : '/admin/notices'
}
